import React from "react";
import { PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import type { CRMChannel } from "@jugl-web/rest-api";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import type { ContactInfoFieldValue } from "../../../../types";

export const ContactIntelligenceFieldValue: React.FC<{
  value: ContactInfoFieldValue;
  onConversationNavigate?: (params: {
    channel: CRMChannel;
    convId: string;
  }) => void;
}> = ({ value, onConversationNavigate }) => {
  const { t } = useTranslations();

  if (value === null) {
    return (
      <span className="text-dark-700 font-secondary jugl__break-word text-sm font-normal leading-[140%]">
        {t({
          id: "contacts-page.contact-info.no-info-found",
          defaultMessage: "No info found",
        })}
      </span>
    );
  }

  switch (value.type) {
    case "plain":
      return (
        <span className="text-dark font-secondary jugl__break-word text-sm font-medium leading-[140%]">
          {value.value}
        </span>
      );
    case "link": {
      const linkClassName =
        "text-primary-800 font-secondary jugl__break-word text-sm font-medium leading-[140%] tracking-[0.12px] underline";

      if (value.href) {
        return (
          <a
            href={value.href}
            target="_blank"
            rel="noreferrer"
            className={linkClassName}
          >
            {value.value}
          </a>
        );
      }

      return <span className={linkClassName}>{value.value}</span>;
    }
    case "conversation":
      if (!value.isChannelAccountConnected) {
        return (
          <div className="flex items-center gap-2">
            <span className="text-dark-600 font-secondary jugl__break-word text-sm font-medium leading-[140%] tracking-[0.12px] underline">
              {value.value}
            </span>
            <Tooltip
              placement="top"
              renderTrigger={({ props, ref }) => (
                <InfoIcon {...props} ref={ref} />
              )}
            >
              {t({
                id: "contacts-page.contact-info.conversation-account-disconnected",
                defaultMessage:
                  "Account was disconnected. Reconnect to see the Conversation with Contact ℹ️",
              })}
            </Tooltip>
          </div>
        );
      }

      return (
        <PlainButton
          className="text-primary-800 font-secondary jugl__break-word text-sm font-medium leading-[140%] tracking-[0.12px] underline"
          onClick={() =>
            onConversationNavigate?.({
              channel: value.channel,
              convId: value.convId,
            })
          }
        >
          {value.value}
        </PlainButton>
      );
    case "notApplicable":
      return (
        <span className="font-secondary jugl__break-word text-sm leading-[140%] text-[#4F4F4F]">
          {t({
            id: "common.not-applicable-short",
            defaultMessage: "N/A",
          })}
        </span>
      );
    case "dash":
      return (
        <span className="text-dark font-secondary jugl__break-word text-sm font-medium leading-[140%]">
          -
        </span>
      );
    default:
      return null;
  }
};
